import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Car, LogOut, Check, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const VisitorExit = () => {
  const { society_code } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [societyName, setSocietyName] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [exitData, setExitData] = useState(null);

  const fetchInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/visitor-checkin/${society_code}/info`);
      setSocietyName(res.data.society_name);
    } catch {
      toast.error('Society not found');
    } finally { setLoading(false); }
  }, [society_code]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const handleExit = async () => {
    if (!vehicleNumber) { toast.error('Enter your vehicle number'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/visitor-exit/${society_code}/scan`, {
        vehicle_number: vehicleNumber
      });
      setExitData(res.data);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F4F4F5' }}>
        <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Loading...</p>
      </div>
    );
  }

  if (!societyName) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#F4F4F5' }}>
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#DC2626' }}>Society Not Found</h1>
          <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>This QR code may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4F4F5' }}>
      {/* Header */}
      <div className="py-4 px-6" style={{ backgroundColor: '#09090B' }}>
        <div className="max-w-lg mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: '#FBBF24', fontFamily: 'IBM Plex Sans, sans-serif' }}>
            ParkShare
          </p>
          <h1 className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#FFFFFF' }}>
            {societyName}
          </h1>
          <p className="text-xs" style={{ color: '#A1A1AA', fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Visitor Exit
          </p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-8">
        {!exitData ? (
          <div className="border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }} data-testid="exit-form">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: '#09090B' }}>
                <LogOut size={22} style={{ color: '#FBBF24' }} />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                  Check Out
                </h2>
                <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                  Enter your vehicle number to exit
                </p>
              </div>
            </div>

            <div className="mb-5">
              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>Vehicle Number</Label>
              <Input
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                className="mt-1.5 border-2 focus:border-black p-3"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '18px' }}
                data-testid="exit-vehicle-input"
              />
            </div>

            <Button
              onClick={handleExit}
              disabled={submitting}
              className="w-full p-4"
              style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold', fontSize: '16px' }}
              data-testid="exit-submit-btn"
            >
              {submitting ? 'Processing...' : 'Check Out'}
            </Button>
          </div>
        ) : (
          <div data-testid="exit-success">
            {/* Success or Overstay banner */}
            <div
              className="border-2 p-6 mb-4 text-center"
              style={{
                borderColor: exitData.is_overstay ? '#F59E0B' : '#10B981',
                backgroundColor: exitData.is_overstay ? '#FEF3C7' : '#D1FAE5'
              }}
            >
              <div
                className="w-14 h-14 mx-auto mb-3 flex items-center justify-center"
                style={{ backgroundColor: exitData.is_overstay ? '#F59E0B' : '#10B981' }}
              >
                {exitData.is_overstay
                  ? <AlertTriangle size={28} style={{ color: '#FFFFFF' }} />
                  : <Check size={28} style={{ color: '#FFFFFF' }} />}
              </div>
              <h2 className="text-xl font-bold mb-1" style={{
                fontFamily: 'Cabinet Grotesk, sans-serif',
                color: exitData.is_overstay ? '#92400E' : '#065F46'
              }}>
                {exitData.is_overstay ? 'Checked Out — Overstay' : 'Checked Out Successfully'}
              </h2>
              {exitData.is_overstay && (
                <p className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#92400E' }}>
                  Overstay by {exitData.overstay_minutes} minute{exitData.overstay_minutes !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Details card */}
            <div className="border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Car size={16} style={{ color: '#71717A' }} />
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Vehicle</span>
                  </div>
                  <span className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                    {exitData.vehicle_number}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Car size={16} style={{ color: '#71717A' }} />
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Spot</span>
                  </div>
                  <span className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                    {exitData.spot_number}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock size={16} style={{ color: '#71717A' }} />
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Check-In</span>
                  </div>
                  <span className="text-sm" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                    {new Date(exitData.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock size={16} style={{ color: '#71717A' }} />
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Check-Out</span>
                  </div>
                  <span className="text-sm" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                    {new Date(exitData.check_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </span>
                </div>
              </div>

              <p className="text-center text-sm mt-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                Thank you for visiting. Drive safe!
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitorExit;
